/**
 * The dictation domain's FLAG MODULE — the whole of ruling 36 for this domain,
 * as amended by the programme owner on 2026-08-29.
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/dictate/flags` is the door
 * that publishes both. `/contributions` carries nothing for flags. Three
 * reasons on the record: the shell registry's `featureFlags` aggregation arm has
 * no production reader, flags are the one descriptor class the declaring domain
 * itself consumes, and reaching the getters through the contributions barrel is
 * the exact graph-contamination path that turned `src/tauri/__tests__/ipc.test.ts`
 * red on `cva`. If that aggregation arm ever gains a real reader,
 * `contributions/feature-flags.ts` comes back as a one-line
 * `export * from '../flags'` and nothing in this file moves.
 *
 * ONE MINTING SITE PER FLAG, which co-location makes structural rather than
 * conventional: the getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts this file carries no flag-key literal at all — the
 * only assertion that can tell a descriptor read from a hardcoded string that
 * happens to be equal today.
 *
 * THE DOOR IS HEADLESS, and it is guarded transitively rather than by
 * inspection. `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file, out of everything under
 * `packages/dictate/src`; `manifest-boundary.test.ts` walks this module's
 * graph ACROSS package boundaries and refuses `@heidi/design-system` and this
 * package's own `/ui` zone anywhere in it, and separately proves the door
 * imports in a bare Node process with no DOM and no side effects. That last one
 * is what keeps a future enumeration consumer (a docs generator, a flag audit)
 * able to read the descriptors without booting a browser.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/dictate/flags` so its own importers, and
 * their `jest.mock` literals, are untouched.
 *
 * WHAT DOES NOT BELONG HERE, each with a live citation in
 * `.ai-context/feature-flags-in-packages.md`:
 *   - a FOREIGN domain's flag. Never import another domain's flag module — the
 *     resolved boolean crosses as a prop or a port at the composition point.
 *     Dictation surfaces read two of those today
 *     (`useIsScribeEnableRtlEnabled`, `useIsWorkspaceSettingsIaEnabled`).
 *   - a fetch/retry-time read. Hooks cannot run in a `queryFn`, so that path
 *     takes the non-React reader below or a config thunk — never a boolean
 *     passed at render, which freezes the flip.
 *   - a getter composed with a mechanism this door does not carry. The PostHog
 *     platform-dictation gate (`release-desktop-dictation` /
 *     `universal-dictation`) is resolved by `posthog-compat`, not by
 *     OpenFeature, so every getter that ANDs it stays app-side until its flag is
 *     door-readable — the standing rule from
 *     `monorepo-wave-1-p3-feature-flags` Z4.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
  usePosthogVariant,
} from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// Dictation ships on every build profile, so each boolean descriptor uses
// `builds: 'all'` instead of the D6 `'cloud-only'` default.
// ---------------------------------------------------------------------------

/** Multilingual verbal commands on the Dictate history page. */
export const dictationVerbalCommandsMultilingualFlag = defineFeatureFlag({
  key: 'release-scribe-dictation-verbal-commands-multilingual',
  defaultValue: false,
  description: 'Multilingual dictation verbal commands',
  builds: 'all',
});

/**
 * Master kill switch for desktop cursor-context awareness. When off, the
 * setting is hidden and Rust never reads the surrounding text.
 */
export const dictationCursorAwarenessFlag = defineFeatureFlag({
  key: 'release-scribe-dictation-cursor-awareness',
  defaultValue: false,
  description: 'Desktop dictation cursor-context awareness',
  builds: 'all',
});

/**
 * Kill switch for the Dictate tour banner and the CTA-opened tour. Raw flag
 * value — the parent `release-desktop-dictation` gate stays a separate AND at
 * the call site, matching web dictation onboarding.
 */
export const dictateTourBannerFlag = defineFeatureFlag({
  key: 'release-scribe-dictate-tour-banner',
  defaultValue: false,
  description: 'Dictate tour banner and the CTA-opened tour',
  builds: 'all',
});

/**
 * Kill switch for the general dictate re-engagement card (SCR-7071). Raw flag
 * value — the parent `release-desktop-dictation` gate stays a separate AND at
 * the call site, matching the tour banner and web dictation onboarding.
 */
export const dictateReengagementCardFlag = defineFeatureFlag({
  key: 'release-scribe-dictate-reengagement-general-card',
  defaultValue: false,
  description: 'General dictate re-engagement card',
  builds: 'all',
});

export const dictateReengagementFlag = defineFeatureFlag({
  key: 'exp-scribe-dictate-reengagement',
  defaultValue: '',
  provider: 'posthog',
  description: 'Dictate re-engagement experiment variant',
});

export const dictationCustomVocabularyFlag = defineFeatureFlag({
  key: 'release-dictation-custom-vocabulary',
  defaultValue: false,
  description: 'Custom vocab tab on the Dictate page (SCR-3728)',
  builds: 'all',
});

/**
 * Milestone-one import journey for Dictate snippets. The platform Dictate
 * rollout remains a separate app-side AND because that gate is PostHog-backed.
 */
export const dictationImportSnippetsFlag = defineFeatureFlag({
  key: 'release-dictation-import-snippets',
  defaultValue: false,
  description: 'Dictate snippet import upload and review journey',
  builds: 'all',
});

/** Device setup flow (phase 2): awareness card with "Set up now", add-device
 * wizard, button mapping. Either phase flag also turns on the dictaphone itself
 * (app-side `useIsDictateHardwareMicEnabled`). Raw flag value — the desktop-only
 * and platform-dictation gates AND at the call site. */
export const dictateDeviceSetupFlag = defineFeatureFlag({
  key: 'release-scribe-dictate-device-setup',
  defaultValue: false,
  description: 'Device setup flow: awareness card, add-device wizard, button mapping',
  builds: 'all',
});

/**
 * Dictate thumbs feedback in the widget and history logs on desktop and web.
 * The widget is available on every build profile, so the rollout gate must be too.
 */
export const dictateUserFeedbackFlag = defineFeatureFlag({
  key: 'release-scribe-dictate-user-feedback',
  defaultValue: false,
  description: 'Dictate output feedback in the widget and history logs',
  builds: 'all',
});

/** Enables the higher feedback-prompt cadence experiment; call sites decide eligibility. */
export const dictateUserFeedbackIncreasedCadenceFlag = defineFeatureFlag({
  key: 'release-scribe-dictate-user-feedback-increased-cadence',
  defaultValue: false,
  description: 'Higher Dictate feedback-prompt cadence experiment',
  builds: 'all',
});

/** Detected-device awareness popup (phase 1): the record button already works.
 * Either phase flag also turns on the dictaphone itself (app-side
 * `useIsDictateHardwareMicEnabled`). Raw flag value — the desktop-only and
 * platform-dictation gates AND at the call site. */
export const dictateDeviceDetectedPopupFlag = defineFeatureFlag({
  key: 'release-scribe-dictate-device-detected-popup',
  defaultValue: false,
  description:
    'Detected-device awareness popup (phase 1): the record button already works',
  builds: 'all',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
// ---------------------------------------------------------------------------

/** Multilingual verbal commands on the Dictate history page. */
export const useIsDictationVerbalCommandsMultilingualEnabled = (): boolean =>
  useBooleanFlag(
    dictationVerbalCommandsMultilingualFlag.key,
    dictationVerbalCommandsMultilingualFlag.defaultValue
  );

/**
 * Master kill switch for desktop cursor-context awareness. When off, the
 * setting is hidden and Rust never reads the surrounding text.
 */
export const useIsCursorAwarenessEnabled = (): boolean =>
  useBooleanFlag(
    dictationCursorAwarenessFlag.key,
    dictationCursorAwarenessFlag.defaultValue
  );

/**
 * The NON-REACT reader for the flag above — this module's whole answer to code
 * that cannot call a hook, and the shape layer (b)'s fetch-time case points at.
 * Its one caller is the native dictation config builder
 * (`src/tauri/dictation/config.ts`), which runs outside React.
 *
 * `readFlag` is the door's own synchronous read over the same OpenFeature
 * domain client the app-side version called directly, so the resolution path is
 * unchanged. It differs in one disclosed way: the door honours the injected
 * `forceEnableAll` override, which the raw client read bypassed. That is the
 * choke point the door exists for (design D8) and it is a dev-only knob, so the
 * delta is a gain rather than a behavioural risk — but it IS a delta and it is
 * recorded here rather than discovered later.
 */
export const isCursorAwarenessEnabled = (): boolean =>
  readFlag(dictationCursorAwarenessFlag.key, dictationCursorAwarenessFlag.defaultValue);

/**
 * Kill switch for the Dictate tour banner and the CTA-opened tour. Raw flag
 * value — the parent `release-desktop-dictation` gate stays a separate AND at
 * the call site, matching web dictation onboarding.
 */
export const useIsDictateTourBannerEnabled = (): boolean =>
  useBooleanFlag(dictateTourBannerFlag.key, dictateTourBannerFlag.defaultValue);

/**
 * Kill switch for the general dictate re-engagement card. Raw flag value —
 * the parent `release-desktop-dictation` gate stays a separate AND at the
 * call site, matching the tour banner and web dictation onboarding.
 */
export const useIsDictateReengagementCardEnabled = (): boolean =>
  useBooleanFlag(
    dictateReengagementCardFlag.key,
    dictateReengagementCardFlag.defaultValue
  );

export const useDictateReengagementFlag = (): {
  isEnabled: boolean;
  isResolved: boolean;
} => {
  const value = usePosthogVariant(dictateReengagementFlag);
  if (value === undefined) return { isEnabled: false, isResolved: false };
  if (value === true) return { isEnabled: true, isResolved: true };
  if (value === false || value === 'control' || value === '')
    return { isEnabled: false, isResolved: true };
  return { isEnabled: true, isResolved: true };
};

export const useIsDictateReengagementEnabled = (): boolean =>
  useDictateReengagementFlag().isEnabled;

export const useIsDictationCustomVocabularyEnabled = (): boolean =>
  useBooleanFlag(
    dictationCustomVocabularyFlag.key,
    dictationCustomVocabularyFlag.defaultValue
  );

export const useIsDictationImportSnippetsEnabled = (): boolean =>
  useBooleanFlag(
    dictationImportSnippetsFlag.key,
    dictationImportSnippetsFlag.defaultValue
  );

/** NON-REACT reader for the flag above — for `buildUserContext` and the native
 *  config builder, which run outside React. */
export const isDictationCustomVocabularyEnabled = (): boolean =>
  readFlag(dictationCustomVocabularyFlag.key, dictationCustomVocabularyFlag.defaultValue);

export const useIsDictateDeviceSetupEnabled = (): boolean =>
  useBooleanFlag(dictateDeviceSetupFlag.key, dictateDeviceSetupFlag.defaultValue);

/** Post-dictation thumbs feedback in the native Dictate widget. */
export const useIsDictateUserFeedbackEnabled = (): boolean =>
  useBooleanFlag(dictateUserFeedbackFlag.key, dictateUserFeedbackFlag.defaultValue);

/** NON-REACT reader for the flag above. The web dictation store decides at
 *  success time (outside React) whether to show the output-feedback prompt and
 *  suppress the star CSAT, so it cannot call the hook. */
export const isDictateUserFeedbackEnabled = (): boolean =>
  readFlag(dictateUserFeedbackFlag.key, dictateUserFeedbackFlag.defaultValue);

/** Higher feedback-prompt cadence experiment on desktop and web. */
export const useIsDictateUserFeedbackIncreasedCadenceEnabled = (): boolean =>
  useBooleanFlag(
    dictateUserFeedbackIncreasedCadenceFlag.key,
    dictateUserFeedbackIncreasedCadenceFlag.defaultValue
  );

/** NON-REACT reader for success-time cadence decisions outside React. */
export const isDictateUserFeedbackIncreasedCadenceEnabled = (): boolean =>
  readFlag(
    dictateUserFeedbackIncreasedCadenceFlag.key,
    dictateUserFeedbackIncreasedCadenceFlag.defaultValue
  );

export const useIsDictateDeviceDetectedPopupEnabled = (): boolean =>
  useBooleanFlag(
    dictateDeviceDetectedPopupFlag.key,
    dictateDeviceDetectedPopupFlag.defaultValue
  );
